import { ArrowLeft, Heart } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const RestingHRDeltaPage = () => {
  const navigate = useNavigate();

  // Weekly resting HR delta data (bpm above baseline)
  const weeklyData = [
    { day: "Mon", value: 3 },
    { day: "Tue", value: 3.5 },
    { day: "Wed", value: 2.5 },
    { day: "Thu", value: 4 },
    { day: "Fri", value: 5 },
    { day: "Sat", value: 3.5 },
    { day: "Sun", value: 4.5 },
  ];

  const maxValue = 6;
  const minValue = 0;
  const baselineValue = 3;

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-8"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Resting HR Delta
          </h1>
        </div>

        {/* Current Delta Card */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                <Heart className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Current Delta
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">--</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    bpm
                  </span>
                </div>
              </div>
            </div>
            <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
        </Card>

        {/* Sympathetic Activation Level */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Sympathetic Activation Level
          </h3>

          {/* Gradient Bar */}
          <div className="relative mb-1 h-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-500 via-yellow-400 to-green-500">
            <div
              className="absolute top-0 h-full w-1 bg-gray-800 dark:bg-white"
              style={{ left: "0%" }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
            <span>Normal</span>
            <span>Mildly elevated</span>
            <span>Elevated</span>
          </div>
        </Card>

        {/* Weekly Resting HR Delta Chart */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-foreground">
            Weekly Resting HR Delta
          </h3>

          {/* Chart */}
          <div className="relative h-48">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 flex h-full flex-col justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>6</span>
              <span>4</span>
              <span>2</span>
              <span>0</span>
            </div>

            {/* Baseline dashed line */}
            <div
              className="absolute left-6 right-0 border-t-2 border-dashed border-gray-400 dark:border-gray-600"
              style={{
                top: `${((maxValue - baselineValue) / maxValue) * 100}%`,
              }}
            />

            {/* Chart area with line graph */}
            <div className="relative ml-6 h-full">
              <svg
                className="h-full w-full"
                viewBox="0 0 280 160"
                preserveAspectRatio="none"
              >
                {/* Line path */}
                <polyline
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                  points={weeklyData
                    .map((item, index) => {
                      const x = (index / (weeklyData.length - 1)) * 280;
                      const y =
                        160 - ((item.value - minValue) / maxValue) * 160;
                      return `${x},${y}`;
                    })
                    .join(" ")}
                />
                {/* Data points */}
                {weeklyData.map((item, index) => {
                  const x = (index / (weeklyData.length - 1)) * 280;
                  const y = 160 - ((item.value - minValue) / maxValue) * 160;
                  return (
                    <circle
                      key={index}
                      cx={x}
                      cy={y}
                      r="4"
                      fill="#ef4444"
                      stroke="white"
                      strokeWidth="1"
                    />
                  );
                })}
              </svg>
            </div>

            {/* X-axis labels */}
            <div className="absolute bottom-0 left-6 right-0 flex justify-around">
              {weeklyData.map((item, index) => (
                <span
                  key={index}
                  className="text-[10px] text-gray-500 dark:text-gray-400"
                >
                  {item.day}
                </span>
              ))}
            </div>
          </div>
        </Card>

        {/* What This Means Section */}
        <Card className="rounded-xl bg-blue-50/80 p-4 shadow-sm dark:bg-blue-900/20">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-600 dark:border-blue-400">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                i
              </span>
            </div>
            <h3 className="text-sm font-semibold text-foreground">
              What This Means
            </h3>
          </div>

          <div className="space-y-3 text-xs text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-semibold">Resting heart rate delta</span> is
              the difference between your current resting HR and your personal
              baseline. A positive delta indicates sympathetic nervous system
              activation.
            </p>

            <p>
              <span className="font-semibold">At +5 bpm above baseline,</span>{" "}
              your body is in a mildly elevated stress state. This can be caused
              by inadequate recovery, dehydration, illness, or emotional stress.
            </p>

            <p>
              <span className="font-semibold">Metabolic impact:</span> Elevated
              resting HR is linked to increased cortisol, which can impair
              insulin sensitivity and affect how your body processes meals.
            </p>
          </div>
        </Card>

        {/* How to Improve Section */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            How to Improve
          </h3>
          <ul className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Stay hydrated — dehydration raises resting HR</span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Practice box breathing (4-4-4-4 pattern)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Reduce training intensity for 24-48 hours</span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Check for signs of overtraining or illness</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default RestingHRDeltaPage;
